'use client';

import React from 'react';
import { AngleImageDisplay } from '@/features/design/concept/ui/AngleImageDisplay';
import { RenderProgress } from './RenderProgress';
import type { Job } from '@/features/design/job-queue';
import { ClassTypes } from '@/shared/constants';
import { AIGlowBorder } from './ai-glow-border/AIGlowBorder';

export interface AngleImageWithProgressProps {
  // Image URLs
  sourceUrl?: string;
  renderUrl?: string;
  alt: string;

  // Display state
  showingRender: boolean;
  onToggle: () => void;
  isUpscaled?: boolean;

  // Active jobs for progress overlays
  activeRenderJob?: Job;
  activeUpscaleJob?: Job;

  // Angle ID for progress tracking
  conceptId?: string;
  projectId?: string;

  // Optional callbacks
  onRenderComplete?: () => void;
  onRenderError?: () => void;
  onUpscaleComplete?: () => void;
  onUpscaleError?: () => void;

  // Swipe navigation (for main angle - navigates between concepts)
  onDraggingChange?: (isDragging: boolean) => void;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;

  // Image click handler (to open fullscreen viewer)
  onImageClick?: () => void;

  // External control for viewer
  isViewerOpenExternal?: boolean;
  onViewerClose?: () => void;

  /** CSS max-height value (e.g., "calc(100vh - 200px)") - preferred */
  maxHeightCss?: string;
  /** Maximum allowed height in pixels (for viewport constraint) - legacy */
  maxHeight?: number;
  /** External programmatic slider position — drives the compare slider
   *  to a target position when render-form mode changes (two-column mode). */
  sliderProgrammaticPosition?: number;

  /** Angle aspect ratio (width / height) known from the API, to seed the
   *  container shape and avoid a reflow when the image loads. */
  initialAspectRatio?: number;

  className?: string;
}

/**
 * Unified component for displaying angle image with render/upscale progress
 *
 * Handles:
 * - Image display with comparison slider
 * - 4K badge for upscaled renders
 * - Render progress overlay (if activeRenderJob)
 * - Upscale progress overlay (if activeUpscaleJob && !activeRenderJob)
 *
 * Use this instead of duplicating progress logic in MainView/SecondaryViews
 */
export function AngleImageWithProgress({
  sourceUrl,
  renderUrl,
  alt,
  // showingRender and onToggle are part of the interface but not used in this component
  // They're kept in the interface for consistency with AngleImageDisplay props
  showingRender: _showingRender,
  onToggle: _onToggle,
  isUpscaled = false,
  activeRenderJob,
  activeUpscaleJob,
  conceptId,
  projectId,
  onRenderComplete,
  onRenderError,
  onUpscaleComplete,
  onUpscaleError,
  onDraggingChange,
  onSwipeLeft,
  onSwipeRight,
  onImageClick,
  isViewerOpenExternal,
  onViewerClose,
  maxHeightCss,
  maxHeight,
  sliderProgrammaticPosition,
  initialAspectRatio,
  className = '',
}: AngleImageWithProgressProps) {
  // Suppress unused variable warnings - these props are part of the interface
  void _showingRender;
  void _onToggle;
  return (
    <div className={`relative ${className}`}>
      {/* Main image display with comparison slider */}
      <AngleImageDisplay
        sourceUrl={sourceUrl}
        renderUrl={renderUrl}
        alt={alt}
        containerClassName=""
        isUpscaled={isUpscaled}
        isRendering={!!activeRenderJob || !!activeUpscaleJob}
        onDraggingChange={onDraggingChange}
        onSwipeLeft={onSwipeLeft}
        onSwipeRight={onSwipeRight}
        onImageClick={onImageClick}
        isViewerOpenExternal={isViewerOpenExternal}
        onViewerClose={onViewerClose}
        maxHeightCss={maxHeightCss}
        maxHeight={maxHeight}
        sliderProgrammaticPosition={sliderProgrammaticPosition}
        initialAspectRatio={initialAspectRatio}
      />

      {/* AI glow border (shows during processing) */}
      <AIGlowBorder isActive={activeRenderJob?.status === 'processing'} />

      {/* Render progress overlay (shows when rendering) */}
      {activeRenderJob && (
        <RenderProgress
          render={{
            type: ClassTypes.RENDER,
            id: activeRenderJob.id,
            attributes: {
              status: activeRenderJob.status,
              started_at: activeRenderJob.startedAt || '',
              duration_predicted: activeRenderJob.durationPredicted,
              priority: 0,
              settings: {},
              setup_hash: '',
              created_at: activeRenderJob.startedAt || '',
              updated_at: activeRenderJob.startedAt || '',
            },
          }}
          mode="inline"
          conceptId={conceptId}
          projectId={projectId}
          onComplete={onRenderComplete}
          onError={onRenderError}
        />
      )}

      {/* Upscale progress overlay (shows when upscaling, but not rendering) */}
      {!activeRenderJob && activeUpscaleJob && (
        <RenderProgress
          queueRenderInfo={activeUpscaleJob}
          mode="inline"
          onComplete={onUpscaleComplete}
          onError={onUpscaleError}
        />
      )}
    </div>
  );
}
